import React from "react";
import { TEXT_COLOR_OPTIONS } from "../components/Helper/JobsiteCommand";

const MAX_TEXT_LENGTH = 140;
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 400;

type CommandCustomTextProps = {
    data?: {
        text?: string;
        color?: string;
    };
};

const CommandCustomText: React.FC<CommandCustomTextProps> = ({ data }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const textRef = React.useRef<HTMLDivElement>(null);
    const [fontSize, setFontSize] = React.useState(MIN_FONT_SIZE);

    const text = (data?.text || "").slice(0, MAX_TEXT_LENGTH);
    const color = data?.color || TEXT_COLOR_OPTIONS[0].value;

    const fitText = React.useCallback(() => {
        const container = containerRef.current;
        const textEl = textRef.current;
        if (!container || !textEl || !text) {
            return;
        }

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        if (containerWidth === 0 || containerHeight === 0) {
            return;
        }

        // Binary search for the largest font size whose wrapped text still fits the container.
        const fits = (size: number) => {
            textEl.style.fontSize = `${size}px`;
            return textEl.scrollWidth <= containerWidth && textEl.scrollHeight <= containerHeight;
        };

        let low = MIN_FONT_SIZE;
        let high = MAX_FONT_SIZE;
        let best = MIN_FONT_SIZE;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            if (fits(mid)) {
                best = mid;
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }

        textEl.style.fontSize = `${best}px`;
        setFontSize(best);
    }, [text]);

    React.useLayoutEffect(() => {
        fitText();
    }, [fitText]);

    React.useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const observer = new ResizeObserver(() => fitText());
        observer.observe(container);

        return () => observer.disconnect();
    }, [fitText]);

    return (
        <div ref={containerRef} className="h-full w-full flex items-center justify-center overflow-hidden px-24">
            <div
                ref={textRef}
                className="font-plot-open-sans text-center wrap-break-word"
                style={{ color, fontSize, lineHeight: 1.2, whiteSpace: "pre-wrap" }}
            >
                {text}
            </div>
        </div>
    );
};

export default CommandCustomText;
